<template>
  <div class="page-wrapper tc-stat">
    <div class="content container-fluid">

      <div class="tc-hdr">
        <div class="tc-title">Thống kê thu chi</div>
        <div class="tc-subtitle">Tổng quan cá nhân</div>
      </div>

      <div class="tc-month-card">
        <button type="button" class="btn btn-sm btn-link tc-month-btn" @click="prevMonth">
          <i class="la la-angle-left"></i>
        </button>
        <div class="tc-month-label">
          Tháng {{ currentMonth }}/{{ currentYear }}
        </div>
        <button type="button" class="btn btn-sm btn-link tc-month-btn" @click="nextMonth">
          <i class="la la-angle-right"></i>
        </button>
      </div>

      <div v-if="loading" class="text-center text-muted py-3">
        Đang tải thống kê...
      </div>

      <template v-else>
        <!-- Tổng thu / chi -->
        <div class="row mt-2">
          <div class="col-md-6 col-12 mb-2">
            <div class="tc-card tc-card-money chi">
              <div class="label">Chi tháng này</div>
              <div class="value">- {{ formatMoney(tongChiThang) }} đ</div>
            </div>
          </div>
          <div class="col-md-6 col-12 mb-2">
            <div class="tc-card tc-card-money thu">
              <div class="label">Thu tháng này</div>
              <div class="value">+ {{ formatMoney(tongThuThang) }} đ</div>
            </div>
          </div>
        </div>

        <!-- Nợ -->
        <div class="row">
          <div class="col-md-6 col-12 mb-2">
            <div class="tc-card tc-card-debt">
              <div class="label">Công ty đang nợ bạn</div>
              <div class="value text-success">
                {{ formatMoney(congTyNoToi) }} đ
              </div>
              <div class="desc small text-muted">
                Các khoản bạn đã chi hộ, sẽ được hoàn qua lương.
              </div>
            </div>
          </div>
          <div class="col-md-6 col-12 mb-2">
            <div class="tc-card tc-card-debt">
              <div class="label">Bạn đang nợ công ty</div>
              <div class="value text-danger">
                {{ formatMoney(toiNoCongTy) }} đ
              </div>
              <div class="desc small text-muted">
                Các khoản tạm ứng / thu hộ sẽ bị trừ lương.
              </div>
            </div>
          </div>
        </div>

        <!-- Hôm nay -->
        <div class="tc-card mt-1">
          <div class="tc-card-header">
            <span>Hôm nay</span>
          </div>
          <div class="row">
            <div class="col-6">
              <div class="tc-today-item chi">
                <div class="label">Chi hôm nay</div>
                <div class="value">- {{ formatMoney(tongChiHomNay) }} đ</div>
              </div>
            </div>
            <div class="col-6">
              <div class="tc-today-item thu">
                <div class="label">Thu hôm nay</div>
                <div class="value">+ {{ formatMoney(tongThuHomNay) }} đ</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Biểu đồ tỷ lệ thu/chi cả tháng -->
        <div class="tc-card mt-2" v-if="tongThuThang > 0 || tongChiThang > 0">
          <div class="tc-card-header d-flex justify-content-between align-items-center">
            <span>Tỷ lệ thu/chi tháng {{ currentMonth }}/{{ currentYear }}</span>
          </div>
          <div class="tc-chart-wrap">
            <canvas ref="chartRatioCanvas" :key="`ratio-${currentMonth}-${currentYear}`"></canvas>
          </div>
          <div class="tc-ratio-legend mt-2">
            <div class="tc-ratio-item">
              <div class="color-dot thu-dot"></div>
              <span>Thu: {{ formatMoney(tongThuThang) }} đ ({{ thuChiRatio.thuPercent }}%)</span>
            </div>
            <div class="tc-ratio-item">
              <div class="color-dot chi-dot"></div>
              <span>Chi: {{ formatMoney(tongChiThang) }} đ ({{ thuChiRatio.chiPercent }}%)</span>
            </div>
          </div>
        </div>

        <!-- Biểu đồ thu/chi theo danh mục -->
        <div class="row mt-2" v-if="hasChartData">
          <div class="col-md-6 col-12 mb-2" v-if="topDanhMucChi.length > 0">
            <div class="tc-card h-100">
              <div class="tc-card-header">
                <span>Chi theo danh mục</span>
              </div>
              <div class="tc-chart-wrap">
                <canvas ref="chartChiCanvas" :key="`chi-${currentMonth}-${currentYear}`"></canvas>
              </div>
            </div>
          </div>
          <div class="col-md-6 col-12 mb-2" v-if="topDanhMucThu.length > 0">
            <div class="tc-card h-100">
              <div class="tc-card-header">
                <span>Thu theo danh mục</span>
              </div>
              <div class="tc-chart-wrap">
                <canvas ref="chartThuCanvas" :key="`thu-${currentMonth}-${currentYear}`"></canvas>
              </div>
            </div>
          </div>
        </div>

        <!-- Trạng thái phiếu -->
        <div class="tc-card mt-2">
          <div class="tc-card-header">
            <span>Trạng thái phiếu tháng này</span>
          </div>
          <div class="tc-status-row">
            <div class="tc-status-pill st-cho-duyet">
              Chờ duyệt: <b>{{ soPhieuChoDuyet }}</b>
            </div>
            <div class="tc-status-pill st-da-duyet">
              Đã duyệt: <b>{{ soPhieuDaDuyet }}</b>
            </div>
            <div class="tc-status-pill st-da-thanh-toan">
              Đã thanh toán: <b>{{ soPhieuDaThanhToan }}</b>
            </div>
            <div class="tc-status-pill st-da-quyet-toan">
              Đã quyết toán: <b>{{ soPhieuDaQuyetToan }}</b>
            </div>
            <div class="tc-status-pill st-tu-choi">
              Từ chối: <b>{{ soPhieuTuChoi }}</b>
            </div>
          </div>

          <div class="tc-status-row mt-1">
            <div class="tc-status-pill border-pill">
              Qua lương: <b>{{ soPhieuQuaLuong }}</b>
            </div>
            <div class="tc-status-pill border-pill">
              Thanh toán / nộp ngay: <b>{{ soPhieuThanhToanNgay }}</b>
            </div>
          </div>
        </div>

        <!-- Top danh mục chi -->
        <div class="tc-card mt-2" v-if="topDanhMucChi.length > 0">
          <div class="tc-card-header d-flex justify-content-between align-items-center">
            <span>Danh mục chi nhiều nhất</span>
          </div>

          <ul class="tc-top-list mt-2">
            <li v-for="(dm, idx) in topDanhMucChi" :key="dm.idDanhMuc || idx" class="tc-top-item">
              <div class="left">
                <div class="rank">{{ idx + 1 }}</div>
                <div class="name">{{ dm.tenDanhMuc }}</div>
                <div class="sub">
                  {{ dm.soLan || 0 }} lần
                </div>
              </div>
              <div class="right">
                <div class="amount">
                  - {{ formatMoney(dm.tongSoTien) }} đ
                </div>
              </div>
            </li>
          </ul>
        </div>

        <!-- Top danh mục thu -->
        <div class="tc-card mt-2" v-if="topDanhMucThu.length > 0">
          <div class="tc-card-header d-flex justify-content-between align-items-center">
            <span>Danh mục thu nhiều nhất</span>
          </div>

          <ul class="tc-top-list mt-2">
            <li v-for="(dm, idx) in topDanhMucThu" :key="dm.idDanhMuc || idx" class="tc-top-item">
              <div class="left">
                <div class="rank">{{ idx + 1 }}</div>
                <div class="name">{{ dm.tenDanhMuc }}</div>
                <div class="sub">
                  {{ dm.soLan || 0 }} lần
                </div>
              </div>
              <div class="right">
                <div class="amount thu-amount">
                  + {{ formatMoney(dm.tongSoTien) }} đ
                </div>
              </div>
            </li>
          </ul>
        </div>
      </template>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from "vue"
import api from "@/api/index.js"
import { useToast } from "vue-toastification"
import Chart from "chart.js/auto"

const toast = useToast()

const now = new Date()
const currentYear = ref(now.getFullYear())
const currentMonth = ref(now.getMonth() + 1)

const loading = ref(false)
const dashboard = ref(null)
const summaryByDay = ref([])

// Chart refs
const chartRatioCanvas = ref(null)
const chartChiCanvas = ref(null)
const chartThuCanvas = ref(null)

let chartRatioInstance = null
let chartChiInstance = null
let chartThuInstance = null

// Thêm biến để theo dõi trạng thái mount
const isComponentMounted = ref(false)

// ===== helper chung =====
function formatMoney(v) {
  if (v == null) return "0"
  const num = Number(v)
  if (Number.isNaN(num)) return "0"
  return num.toLocaleString("vi-VN")
}

function getNumber(field) {
  if (!dashboard.value || dashboard.value[field] == null) return 0
  const v = Number(dashboard.value[field])
  return Number.isNaN(v) ? 0 : v
}

function getLong(field) {
  if (!dashboard.value || dashboard.value[field] == null) return 0
  const v = Number(dashboard.value[field])
  return Number.isNaN(v) ? 0 : Math.trunc(v)
}

// Chuẩn hoá response summary-by-day
function normalizeSummaryList(data) {
  if (!data) return []
  if (Array.isArray(data)) return data
  return []
}

// ===== computed tổng tháng & nợ =====
const tongChiThang = computed(() => getNumber("tongChiThang"))
const tongThuThang = computed(() => getNumber("tongThuThang"))
const congTyNoToi = computed(() => getNumber("congTyNoToi"))
const toiNoCongTy = computed(() => getNumber("toiNoCongTy"))

// Tính tỷ lệ thu chi
const thuChiRatio = computed(() => {
  const thu = tongThuThang.value
  const chi = tongChiThang.value
  const total = thu + chi
  
  if (total === 0) return { thuPercent: 0, chiPercent: 0 }
  
  return {
    thuPercent: Math.round((thu / total) * 100),
    chiPercent: Math.round((chi / total) * 100)
  }
})

// Kiểm tra có dữ liệu biểu đồ không
const hasChartData = computed(() => {
  return topDanhMucChi.value.length > 0 || topDanhMucThu.value.length > 0
})

// hôm nay – ưu tiên lấy từ summary-by-day
const tongChiHomNay = computed(() => {
  const todayISO = new Date().toISOString().slice(0, 10)
  const found = (summaryByDay.value || []).find(
    x => {
      const ngay = x.ngay
      if (typeof ngay === 'string') return ngay.startsWith(todayISO)
      return false
    }
  )
  if (found && found.tongChi != null) {
    const v = Number(found.tongChi)
    return Number.isNaN(v) ? 0 : v
  }
  return getNumber("tongChiHomNay")
})

const tongThuHomNay = computed(() => {
  const todayISO = new Date().toISOString().slice(0, 10)
  const found = (summaryByDay.value || []).find(
    x => {
      const ngay = x.ngay
      if (typeof ngay === 'string') return ngay.startsWith(todayISO)
      return false
    }
  )
  if (found && found.tongThu != null) {
    const v = Number(found.tongThu)
    return Number.isNaN(v) ? 0 : v
  }
  return getNumber("tongThuHomNay")
})

// trạng thái phiếu
const soPhieuChoDuyet = computed(() => getLong("soPhieuChoDuyet"))
const soPhieuDaDuyet = computed(() => getLong("soPhieuDaDuyet"))
const soPhieuDaThanhToan = computed(() => getLong("soPhieuDaThanhToan"))
const soPhieuDaQuyetToan = computed(() => getLong("soPhieuDaQuyetToan"))
const soPhieuTuChoi = computed(() => getLong("soPhieuTuChoi"))
const soPhieuQuaLuong = computed(() => getLong("soPhieuQuaLuong"))
const soPhieuThanhToanNgay = computed(() => getLong("soPhieuThanhToanNgay"))

// top danh mục chi
const topDanhMucChi = computed(() => {
  const raw = dashboard.value?.topDanhMucChi
  if (!raw) return []
  if (Array.isArray(raw)) return raw.slice(0, 8) // Giới hạn hiển thị
  return []
})

// top danh mục thu - giả định có field này, nếu không có thì trả về mảng rỗng
const topDanhMucThu = computed(() => {
  const raw = dashboard.value?.topDanhMucThu
  if (!raw) return []
  if (Array.isArray(raw)) return raw.slice(0, 8) // Giới hạn hiển thị
  return []
})

// Màu sắc cho biểu đồ
const chartColors = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', 
  '#9966FF', '#FF9F40', '#FF6384', '#C9CBCF',
  '#7EB3FF', '#FF9666', '#C5FF67', '#FF67E4',
  '#67FFCE', '#FF6767', '#B367FF', '#FFB367'
]

// ===== API load dashboard =====
async function loadDashboard() {
  loading.value = true
  try {
    const res = await api.get("/api/thu-chi/my/dashboard", {
      params: {
        thang: currentMonth.value,
        nam: currentYear.value
      }
    })
    dashboard.value = res.data || {}
    console.log("Dashboard thu chi:", dashboard.value)
  } catch (e) {
    console.error("Lỗi load dashboard:", e)
    toast.error("Không tải được thống kê thu chi")
    dashboard.value = null
  } finally {
    loading.value = false
  }
}

// ===== API load summary-by-day =====
async function loadSummaryByDay() {
  try {
    const res = await api.get("/api/thu-chi/my/summary-by-day", {
      params: {
        thang: currentMonth.value,
        nam: currentYear.value
      }
    })
    const list = normalizeSummaryList(res.data)
    summaryByDay.value = list.map(row => ({
      ...row,
      tongChi: Number(row.tongChi || 0),
      tongThu: Number(row.tongThu || 0),
      soPhieu: Number(row.soPhieu || 0)
    }))
  } catch (e) {
    console.error("Lỗi load summary-by-day:", e)
    summaryByDay.value = []
  }
}

// ===== điều khiển tháng =====
async function prevMonth() {
  let y = currentYear.value
  let m = currentMonth.value - 1
  if (m < 1) {
    m = 12
    y -= 1
  }
  currentYear.value = y
  currentMonth.value = m
  await reloadData()
}

async function nextMonth() {
  let y = currentYear.value
  let m = currentMonth.value + 1
  if (m > 12) {
    m = 1
    y += 1
  }
  currentYear.value = y
  currentMonth.value = m
  await reloadData()
}

async function reloadData() {
  destroyAllCharts()
  await Promise.all([loadDashboard(), loadSummaryByDay()])
}

// ===== Chart: Tỷ lệ thu/chi =====
async function renderRatioChart() {
  if (!isComponentMounted.value) return
  
  const thu = tongThuThang.value
  const chi = tongChiThang.value

  if (thu === 0 && chi === 0) {
    destroyChart('ratio')
    return
  }

  await nextTick()
  
  if (!isComponentMounted.value || !chartRatioCanvas.value) return
  
  const ctx = chartRatioCanvas.value.getContext("2d")
  if (!ctx) return

  destroyChart('ratio')

  try {
    chartRatioInstance = new Chart(ctx, {
      type: "doughnut",
      data: {
        labels: ['Thu', 'Chi'],
        datasets: [{
          data: [thu, chi],
          backgroundColor: ['#43a047', '#f44336'],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = Number(context.parsed || 0)
                const total = thu + chi
                const percent = total > 0 ? (value * 100) / total : 0
                return `${context.label}: ${value.toLocaleString("vi-VN")} đ (${percent.toFixed(1)}%)`
              }
            }
          }
        }
      }
    })
  } catch (error) {
    console.error("Lỗi khi render chart tỷ lệ:", error)
  }
}

// ===== Chart: Danh mục chi =====
async function renderChiChart() {
  if (!isComponentMounted.value) return
  
  const list = topDanhMucChi.value
  if (!list.length) {
    destroyChart('chi')
    return
  }

  await nextTick()
  
  if (!isComponentMounted.value || !chartChiCanvas.value) return
  
  const ctx = chartChiCanvas.value.getContext("2d")
  if (!ctx) return

  const labels = list.map(x => x.tenDanhMuc || "Không tên")
  const data = list.map(x => Number(x.tongSoTien || 0))

  destroyChart('chi')

  try {
    chartChiInstance = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: chartColors,
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = Number(context.parsed || 0)
                const total = data.reduce((sum, v) => sum + v, 0)
                const percent = total > 0 ? (value * 100) / total : 0
                return `${context.label}: ${value.toLocaleString("vi-VN")} đ (${percent.toFixed(1)}%)`
              }
            }
          }
        }
      }
    })
  } catch (error) {
    console.error("Lỗi khi render chart chi:", error)
  }
}

// ===== Chart: Danh mục thu =====
async function renderThuChart() {
  if (!isComponentMounted.value) return
  
  const list = topDanhMucThu.value
  if (!list.length) {
    destroyChart('thu')
    return
  }

  await nextTick()
  
  if (!isComponentMounted.value || !chartThuCanvas.value) return
  
  const ctx = chartThuCanvas.value.getContext("2d")
  if (!ctx) return

  const labels = list.map(x => x.tenDanhMuc || "Không tên")
  const data = list.map(x => Number(x.tongSoTien || 0))

  destroyChart('thu')

  try {
    chartThuInstance = new Chart(ctx, {
      type: "pie",
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: chartColors,
          borderWidth: 2,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const value = Number(context.parsed || 0)
                const total = data.reduce((sum, v) => sum + v, 0)
                const percent = total > 0 ? (value * 100) / total : 0
                return `${context.label}: ${value.toLocaleString("vi-VN")} đ (${percent.toFixed(1)}%)`
              }
            }
          }
        }
      }
    })
  } catch (error) {
    console.error("Lỗi khi render chart thu:", error)
  }
}

// Hàm hủy chart
function destroyChart(type) {
  if (type === 'ratio' && chartRatioInstance) {
    chartRatioInstance.destroy()
    chartRatioInstance = null
  }
  if (type === 'chi' && chartChiInstance) {
    chartChiInstance.destroy()
    chartChiInstance = null
  }
  if (type === 'thu' && chartThuInstance) {
    chartThuInstance.destroy()
    chartThuInstance = null
  }
}

// Hàm hủy tất cả charts
function destroyAllCharts() {
  destroyChart('ratio')
  destroyChart('chi')
  destroyChart('thu')
}

// Watchers
watch(
  () => [tongThuThang.value, tongChiThang.value, currentMonth.value, currentYear.value],
  async () => {
    if (isComponentMounted.value) {
      await nextTick()
      await renderRatioChart()
    }
  },
  { immediate: true, flush: 'post' }
)

watch(
  () => [topDanhMucChi.value, currentMonth.value, currentYear.value],
  async () => {
    if (isComponentMounted.value) {
      await nextTick()
      await renderChiChart()
    }
  },
  { immediate: true, flush: 'post' }
)

watch(
  () => [topDanhMucThu.value, currentMonth.value, currentYear.value],
  async () => {
    if (isComponentMounted.value) {
      await nextTick()
      await renderThuChart()
    }
  },
  { immediate: true, flush: 'post' }
)

onMounted(async () => {
  isComponentMounted.value = true
  await reloadData()
})

onBeforeUnmount(() => {
  isComponentMounted.value = false
  destroyAllCharts()
})
</script>

<style scoped>
.tc-chart-wrap {
  height: 260px;
  margin-top: 4px;
  position: relative;
}

.tc-stat {
  background: #f6f8fb;
  min-height: calc(100vh - 80px);
  display: flex;
  justify-content: center;
}

.tc-stat .content {
  max-width: 480px;
}

@media (min-width: 992px) {
  .tc-stat .content {
    max-width: 720px;
  }
}

/* header */
.tc-hdr {
  padding: 4px 2px 4px;
}

.tc-title {
  font-weight: 600;
  font-size: 18px;
}

.tc-subtitle {
  font-size: 12px;
  color: #888;
}

/* month selector */
.tc-month-card {
  margin-top: 4px;
  background: #fff;
  border-radius: 12px;
  padding: 6px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
}

.tc-month-label {
  font-weight: 600;
  font-size: 15px;
}

.tc-month-btn {
  color: #ff8b00;
}

/* cards */
.tc-card {
  background: #fff;
  border-radius: 12px;
  padding: 10px 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
}

.tc-card-money {
  color: #fff;
}

.tc-card-money.chi {
  background: linear-gradient(135deg, #f44336, #f57c00);
}

.tc-card-money.thu {
  background: linear-gradient(135deg, #43a047, #009688);
}

.tc-card-money .label {
  font-size: 12px;
  opacity: 0.9;
}

.tc-card-money .value {
  font-size: 18px;
  font-weight: 700;
}

.tc-card-debt .label {
  font-size: 13px;
  font-weight: 600;
}

.tc-card-debt .value {
  font-size: 16px;
  margin-top: 2px;
}

.tc-card-debt .desc {
  margin-top: 2px;
}

.tc-card-header {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
}

/* hôm nay */
.tc-today-item {
  padding: 6px 0;
}

.tc-today-item .label {
  font-size: 12px;
  color: #777;
}

.tc-today-item .value {
  font-size: 15px;
  font-weight: 600;
}

.tc-today-item.chi .value {
  color: #e53935;
}

.tc-today-item.thu .value {
  color: #43a047;
}

/* status pills */
.tc-status-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tc-status-pill {
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 999px;
}

.st-cho-duyet {
  background: #fff3cd;
  color: #856404;
}

.st-da-duyet {
  background: #e3f2fd;
  color: #1565c0;
}

.st-da-thanh-toan {
  background: #e8f5e9;
  color: #2e7d32;
}

.st-da-quyet-toan {
  background: #ede7f6;
  color: #5e35b1;
}

.st-tu-choi {
  background: #ffebee;
  color: #c62828;
}

.border-pill {
  border: 1px solid #ddd;
  background: #fafafa;
}

/* Tỷ lệ thu chi legend */
.tc-ratio-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tc-ratio-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.thu-dot {
  background: #43a047;
}

.chi-dot {
  background: #f44336;
}

/* chart + top list */
.tc-top-list {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.tc-top-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-top: 1px solid #f1f1f1;
}

.tc-top-item:first-child {
  border-top: none;
}

.tc-top-item .left {
  display: flex;
  align-items: center;
}

.tc-top-item .rank {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #fff8ec;
  color: #ff8b00;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
  font-weight: 600;
}

.tc-top-item .name {
  font-size: 13px;
  font-weight: 600;
}

.tc-top-item .sub {
  font-size: 11px;
  color: #777;
}

.tc-top-item .right .amount {
  font-size: 13px;
  font-weight: 600;
  color: #e53935;
}

.tc-top-item .right .thu-amount {
  color: #43a047;
}

/* Responsive cho chart cards */
@media (max-width: 767px) {
  .tc-chart-wrap {
    height: 220px;
  }
}

.h-100 {
  height: 100%;
}
.tc-stat {
  background: #f6f8fb;
  min-height: calc(100vh - 80px);
  display: flex;
  justify-content: center;
}

.tc-stat .content {
  width: 100%;
  max-width: 480px;
}

/* Tablet */
@media (min-width: 768px) {
  .tc-stat .content {
    max-width: 720px;
  }

  .tc-card {
    padding: 16px;
  }

  .tc-chart-wrap {
    height: 280px;
  }
}

/* Desktop */
@media (min-width: 1200px) {
  .tc-stat .content {
    max-width: 800px;
  }

  .tc-card {
    padding: 20px;
  }

  .tc-chart-wrap {
    height: 300px;
  }
}

/* Điều chỉnh grid cho tổng thu/chi và nợ */
@media (min-width: 768px) {
  .row {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -8px;
  }

  .col-md-6 {
    padding: 0 8px;
    flex: 0 0 50%;
    max-width: 50%;
  }
}
</style>